<template>
  <div class="cv-container">
    <table class="table">
      <tbody>
        <tr>
          <td>
            <article
              :class="`flow along-column theme-${options.theme} skill-theme-${options.skillTheme}`"
            >
              <section id="heading">
                <div id="heading-background">
                  <div
                    v-if="options.branding.includes('skry background')"
                    id="skry-background"
                    class="flow from-right bg-logo-group"
                    :class="{ 'temp-large-bg': useLargePattern }"
                  >
                    <div
                      v-for="{ id, variants } in [
                        { id: 0, variants: ['skry', 'skry', 'skr'] },
                        { id: 1, variants: ['skry', 'skry', 'skry'] },
                        { id: 2, variants: ['skry', 'skry', 'skry'] },
                        { id: 3, variants: ['skry', 'skry', 'skr'] },
                        { id: 4, variants: ['sk'] },
                      ]"
                      :key="id"
                      class="flow along-column bg-logo-container"
                    >
                      <div v-for="(variant, i) in variants" :key="i" class="stacked-logo">
                        <StackedLogo :letters="variant" :theme="options.theme" />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="rebranding && options.branding.includes('rebrand big')"
                    id="rebrand-big"
                    class="flow from-right rebranding"
                  >
                    <div>
                      <img :src="rebranding" />
                    </div>
                  </div>
                </div>
                <div v-if="showTriangle" id="heading-triangle" />
                <div v-if="cv" id="heading-content" class="content">
                  <div class="flow gap-8">
                    <div v-if="hasVisibleGravatar" class="relative">
                      <a-avatar
                        :src="cv.gravatar"
                        :size="128"
                        :shape="showRoundAvatar ? 'circle' : 'square'"
                      />
                      <span v-if="hasVisibleAvailability" class="available-badge-container">
                        <AvailableBadge
                          :type="props.cv.availability_type"
                          :availability-date="props.cv.availability_date"
                          :notice-period-days="props.cv.notice_period_days"
                        />
                      </span>
                    </div>
                    <div class="flow along-column">
                      <div class="flow space-between">
                        <div>
                          <img
                            v-if="rebranding && options.branding.includes('rebrand left')"
                            class="logo"
                            :src="rebranding"
                          />
                          <img
                            v-else-if="options.branding.includes('skry')"
                            class="logo"
                            :src="logo"
                          />
                        </div>
                        <div class="flow gap-8">
                          <img
                            v-if="rebranding && options.branding.includes('rebrand right')"
                            class="logo"
                            :src="rebranding"
                          />
                          <AvailableBadge
                            v-if="hasVisibleAvailability && !hasVisibleGravatar"
                            :type="props.cv.availability_type"
                            :availability-date="props.cv.availability_date"
                            :notice-period-days="props.cv.notice_period_days"
                          />
                        </div>
                      </div>
                      <div>
                        <h2 class="name">{{ cv.name }}</h2>
                        <h3 class="cv-title semi-bold">
                          <span class="separating-dot">{{ cv.first_title }}</span>
                          <span v-if="!!cv.second_title">{{ cv.second_title }}</span>
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div class="flow flex-start space-between">
                    <div class="background">{{ cv.background }}</div>
                    <div class="flow along-column space-between characteristics">
                      <div
                        v-for="{ id, title } in cv?.characteristics"
                        :key="id"
                        class="right-arrow semi-bold"
                      >
                        {{ title }}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section id="cv" class="content gap-12">
                <section
                  v-if="experience.filter((exp) => exp.featured).length > 0"
                  id="featured-project"
                >
                  <h2 class="section-title">
                    {{ t("CV.featuredProjects") }}
                  </h2>
                  <div class="flow along-column pad-10">
                    <div
                      v-for="(exp, i) in experience.filter((exp) => exp.featured)"
                      :key="i"
                      class="flow along-column break-avoiding"
                    >
                      <h3 class="bold featured-project-title truncate">
                        {{ exp.title }}
                      </h3>
                      <div class="flow">
                        <span class="separating-dot bold">
                          <span class="truncate">
                            {{ exp.company_name }}
                          </span>
                        </span>
                        <span>{{ formatProjectDate(exp.start_date, exp.end_date, true) }}</span>
                      </div>
                    </div>
                  </div>
                </section>
                <section v-if="cv" id="relevant-skills">
                  <h2 class="section-title">{{ t("CV.relevantSkills") }}</h2>
                  <div class="flow along-column gap-8">
                    <div class="flow wrap">
                      <!-- TODO: Optimize :class when backend support is added -->
                      <div
                        v-for="skill in skillsToCvSkills(cv?.skills).filter((s) => s.highlight)"
                        :key="skill.id"
                        class="skill-card"
                        :class="{
                          bold: skill.highlight && options.highlighting.includes('bold'),
                          'no-color': !skill.highlight && options.highlighting.includes('color'),
                          raise: skill.highlight && options.highlighting.includes('raise'),
                        }"
                        :style="{
                          fontSize: 13 * skillCategoryScale + 'px',
                          padding: 8 * skillCategoryScale + 'px ' + 12 * skillCategoryScale + 'px',
                          margin: 4 * skillCategoryScale + 'px ' + 8 * skillCategoryScale + 'px',
                          marginLeft: 0,
                        }"
                      >
                        {{ skill.title }}
                      </div>
                    </div>
                  </div>
                </section>
                <section
                  v-if="showEducations && cv && !!cv?.educations && cv.educations.length"
                  id="education"
                >
                  <h2 class="section-title">
                    {{ t("CV.education") }}
                  </h2>
                  <div class="flow along-column pad-10">
                    <div v-for="education in cv.educations" :key="education.id">
                      <h3 class="title">{{ education.title }}</h3>
                      <!-- TODO: Add location option when adding education in profile -->
                      <div class="flow along-column gap-4">
                        <span>{{ education.university_name }}</span>
                        <span
                          v-if="
                            showEducationsDescription &&
                            cv &&
                            !!cv?.educations &&
                            cv.educations.length
                          "
                          >{{ education.description }}</span
                        >
                      </div>
                    </div>
                  </div>
                </section>
              </section>
              <section
                v-if="cv?.knowledge_domains.length && showAreasOfExpertise"
                id="knowledge-domains"
                class="content"
              >
                <h2 class="section-title">{{ t("CV.areasOfExpertise") }}</h2>
                <div class="section-container gap-6">
                  <div v-for="domain in cv?.knowledge_domains" :key="domain.id" class="stack gap-4">
                    <div v-if="!!domain.level" class="flow space-between flex-start">
                      <h3 class="bold slim self-center">{{ domain.title }}</h3>
                      <span class="title-card">{{ domain.level }}</span>
                    </div>
                    <span>{{ domain.description }}</span>
                  </div>
                </div>
              </section>
              <section v-if="cv && experience.length > 0" id="projects" class="content">
                <h2 class="section-title">
                  {{ t("CV.selectedProjects") }}
                </h2>
                <div class="flow along-column pad-10">
                  <div
                    v-for="(exp, i) in experience"
                    :key="i"
                    class="flow along-column break-avoiding"
                  >
                    <div class="flow space-between flex-start">
                      <h3 class="bold slim self-center">{{ exp.company_name }}</h3>
                      <span class="title-card">{{ exp.title }}</span>
                    </div>
                    <div class="flow along-column gap-4 selected-project-content">
                      <div>
                        <span v-if="!!showDates" class="hint separating-dot">
                          {{ formatProjectDate(exp.start_date, exp.end_date) }}
                        </span>
                        <span v-if="!!showDuration" class="hint separating-dot">
                          {{ formatProjectDuration(exp.start_date, exp.end_date) }}
                        </span>
                      </div>
                      <span>{{ exp.description }}</span>
                      <span v-if="!!exp.url_link">
                        <a :href="exp.url_link" target="_blank">{{ exp.url_link }}</a>
                      </span>
                      <div v-if="showSkills && exp.skills.length" class="flow wrap">
                        <div
                          v-for="skill in exp.skills"
                          :key="skill.id"
                          class="skill-card"
                          :class="{
                            bold: skill.highlight && options.highlighting.includes('bold'),
                            'no-color': !skill.highlight && options.highlighting.includes('color'),
                            raise: skill.highlight && options.highlighting.includes('raise'),
                          }"
                          :style="{
                            fontSize: 13 * skillCategoryScale + 'px',
                            padding:
                              8 * skillCategoryScale + 'px ' + 12 * skillCategoryScale + 'px',
                            margin: 4 * skillCategoryScale + 'px ' + 8 * skillCategoryScale + 'px',
                            marginLeft: 0,
                          }"
                        >
                          {{ skill.title }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section v-if="cv?.courses.length" id="courses" class="content">
                <h2 class="section-title">{{ t("CV.certificationsAndCourses") }}</h2>
                <div class="section-container gap-6">
                  <div v-for="course in cv?.courses" :key="course.id" class="stack gap-4">
                    <h3 class="bold slim">{{ course.title }}</h3>
                    <!-- Course duration is here in design, but we do not currently store course duration information -->
                    <span>{{ course.description }}</span>
                  </div>
                </div>
              </section>
              <div v-if="cv" class="page-footer content">
                <div>
                  <span>{{ cv.contact_email }}</span>
                </div>
                <div>
                  <span>{{ new Date(Date.now()).toDateString() }}</span>
                </div>
              </div>
            </article>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="cv">
        <tr>
          <td>
            <div class="page-footer-space" />
          </td>
        </tr>
      </tfoot>
    </table>
    <div id="cv-controls">
      <div class="flow along-column gap-1">
        <h1>Controls for Skry V2</h1>
        <a-select
          v-model="options.theme"
          size="small"
          class="mr-2 dropdown-select"
          :disabled="true"
        >
          <a-select-option v-for="color in themes" :key="color" :value="color">
            {{ color.charAt(0).toUpperCase() + color.slice(1) }} Header
          </a-select-option>
        </a-select>

        <a-select v-model="options.skillTheme" size="small" class="mr-2 dropdown-select">
          <a-select-option v-for="color in themes" :key="color" :value="color">
            {{ color.charAt(0).toUpperCase() + color.slice(1) }} Skills
          </a-select-option>
        </a-select>

        <a-select
          v-model="options.highlighting"
          mode="tags"
          placeholder="Skill highlight"
          size="small"
          class="mr-2"
        >
          <a-select-option value="" disabled>Skill highlight</a-select-option>
          <a-select-option v-for="h in highlighting" :key="h" :value="h">
            {{ h.charAt(0).toUpperCase() + h.slice(1) }}
          </a-select-option>
        </a-select>

        <a-select
          v-model="options.branding"
          mode="tags"
          placeholder="Branding"
          size="small"
          class="mr-2"
        >
          <a-select-option value="" disabled>Branding</a-select-option>
          <a-select-option v-for="b in brandingOptions" :key="b" :value="b">
            {{ b.charAt(0).toUpperCase() + b.slice(1) }}
          </a-select-option>
        </a-select>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { cvUserDataInterface, SkillCvInterface, CvOptionsInterface } from "@/modules/cv/types";
import StackedLogo from "@/modules/cv/_components/StackedLogo.vue";
import { SkillInterface } from "@/modules/consultant_profiles/types";
import { SkillInterface as SkillInterface2 } from "@/modules/skills/types";
import { whiteLogoDataURL } from "@/modules/cv/_utils/logoData";
import { mapWorkExperiencesForPreview } from "@/modules/cv/_utils/previewWorkExperiences";
import { useI18n, LanguageCode } from "@/i18n/index";
import AvailableBadge from "../AvailableBadge.vue";

const { t } = useI18n();

// Types
type Experience = {
  title: string;
  company_name: string;
  description: string;
  start_date: string;
  end_date: string;
  skills: Array<SkillCvInterface>;
  url_link?: string;
  featured?: boolean;
};

// Constants
const themes = ["black", "turquoise", "purple", "red", "yellow", "green", "blue"];
const highlighting = ["bold", "color", "raise"];
const brandingOptions = ["skry", "skry background", "rebrand left", "rebrand right", "rebrand big"];

const props = defineProps({
  cv: { type: Object as () => cvUserDataInterface, default: undefined },
  languageCode: { type: String, default: LanguageCode.English },
  showDates: { type: Boolean, default: undefined },
  showDuration: { type: Boolean, default: undefined },
  showAreasOfExpertise: { type: Boolean, default: undefined },
  showSkills: { type: Boolean, default: undefined },
  showGithub: { type: Boolean, default: undefined },
  showGravatar: { type: Boolean, default: undefined },
  showAvailability: { type: Boolean, default: undefined },
  showEducations: { type: Boolean, default: undefined },
  showEducationsDescription: { type: Boolean, default: undefined },
  showEducationDates: { type: Boolean, default: undefined },
  showEducationSkills: { type: Boolean, default: undefined },
  skillCategoryScale: { type: Number, default: 1 },
  cvBackground: { type: Array as () => string[], default: () => [] },
  cvLogo: { type: String as () => string | undefined, default: () => undefined },
  formatProjectDate: {
    type: Function,
    default: (start: any, end: any, shortMonth: Boolean) => String,
  },
  formatProjectDuration: {
    type: Function,
    default: (start: string, end: string) => String,
  },
  cvOptions: { type: Object as () => CvOptionsInterface, default: undefined },
});
const logo = computed<string>(() => whiteLogoDataURL());
const hasVisibleGravatar = computed<boolean>(
  () => !!(props.showGravatar && props.cv?.gravatar && props.cv.gravatar.length > 1)
);
const hasVisibleAvailability = computed<boolean>(
  () => !!(props.showAvailability && props.cv?.availability_type)
);
const useLargePattern = computed<boolean>(() => {
  switch (options.value.theme) {
    case "black":
      return false;
    case "turquoise":
    case "purple":
    case "red":
    case "yellow":
    case "green":
    case "blue":
    default:
      return true;
  }
});
const showTriangle = computed<boolean>(() => {
  switch (options.value.theme) {
    case "black":
      return false;
    case "turquoise":
    case "purple":
    case "red":
    case "yellow":
    case "green":
    case "blue":
    default:
      return true;
  }
});
const showRoundAvatar = computed<boolean>(() => {
  switch (options.value.theme) {
    case "black":
      return false;
    case "turquoise":
    case "purple":
    case "red":
    case "yellow":
    case "green":
    case "blue":
    default:
      return true;
  }
});

const options = ref<CvOptionsInterface>({
  theme: props.cvOptions?.theme ?? themes[0],
  skillTheme: props.cvOptions?.skillTheme ?? themes[0],
  highlighting: props.cvOptions?.highlighting ?? [],
  branding: props.cvOptions?.branding ?? [brandingOptions[0], brandingOptions[1]],
});

const emits = defineEmits(["set-options"]);
const cvWorkExperiences = computed(() => props.cv?.work_experiences ?? []);
const cvSkills = computed(() => props.cv?.skills ?? []);

watch(
  () => options.value,
  (newOpt: CvOptionsInterface) => {
    options.value = newOpt;
    emits("set-options", newOpt);
  },
  { deep: true }
);

const experience = computed<Experience[]>(() => {
  return mapWorkExperiencesForPreview(cvWorkExperiences.value).map((experience) => ({
    title: experience.title,
    company_name: experience.company_name,
    description: experience.description,
    start_date: experience.start_date,
    end_date: experience.end_date,
    featured: experience.featured,
    skills: skillsToCvSkills(experience.skills),
    url_link: experience.url_link,
  }));
});
const rebranding = computed<string | undefined>(() => props.cvLogo);

// We need to switch body background color for correct color during print
let previousBackgroundColor = "#f4f4f4";
onMounted(() => {
  previousBackgroundColor = document.body.style.background;
  // Background color should match scss variable $background
  document.body.style.background = "#ffffff";
});
// Restore previous background color
onUnmounted(() => {
  document.body.style.background = previousBackgroundColor;
});

// TODO Optimize once backend supports returning SkillsCvInterface for work/project experience
const skillsToCvSkills = (skills: (SkillInterface | SkillInterface2)[]): SkillCvInterface[] =>
  skills.map((s) => ({
    ...s,
    skill_id: s.id,
    highlight: false,
    description: s.description ?? "",
    ...cvSkills.value.find((cvSkill) => cvSkill.skill_id === s.id),
  }));
</script>

<style scoped lang="scss">
$background: #ffffff;
$footer-background: #9da1a21a;
$footer-text: #7c7c7c;
$text-dark: #000000;
$text-dark-faded: #00000099;
$text-light: #ffffff;
$text-light-faded: #ffffff99;
$text-gray: #7d7d7d;
$default-border: 1px solid #ebebeb;
$footer-height: 10mm;
$cv-width: 210mm;

$separating-dot-spacing: 6mm;
$right-arrow-spacing: 3.35mm;
$triangle-size: 70mm;

$theme-triangle: #9da1a2;
$theme-title-border: #1a1a1a;
$theme-title-card-border: #d6d6d6;

$skill-card-shadow: #cccccc;

$black-heading-background: #1a1a1a;
$black-skill-card: #eeeeee;

$gradient-angle: 76.99deg;
$gradient-start: 0%;
$gradient-end: 100%;

$turquoise-gradient-start: hsl(168, 84%, 80%);
$turquoise-gradient-end: hsl(157, 94%, 93%);
$turquoise-skill-card: hsl(164, 62%, 92%);
$turquoise-heading-background: linear-gradient(
  $gradient-angle,
  $turquoise-gradient-start $gradient-start,
  $turquoise-gradient-end $gradient-end
);

$purple-gradient-start: hsl(257, 84%, 80%);
$purple-gradient-end: hsl(260, 84%, 83%);
$purple-skill-card: hsl(293, 53%, 93%);
$purple-heading-background: linear-gradient(
  $gradient-angle,
  $purple-gradient-start $gradient-start,
  $purple-gradient-end $gradient-end
);

$red-gradient-start: hsl(0, 84%, 80%);
$red-gradient-end: hsl(1, 87%, 85%);
$red-skill-card: hsl(5, 63%, 93%);
$red-heading-background: linear-gradient(
  $gradient-angle,
  $red-gradient-start $gradient-start,
  $red-gradient-end $gradient-end
);

$yellow-gradient-start: hsl(51, 82%, 80%);
$yellow-gradient-end: hsl(52, 86%, 92%);
$yellow-skill-card: hsl(51, 64%, 91%);
$yellow-heading-background: linear-gradient(
  $gradient-angle,
  $yellow-gradient-start $gradient-start,
  $yellow-gradient-end $gradient-end
);

$green-gradient-start: hsl(110, 77%, 81%);
$green-gradient-end: hsl(127, 92%, 95%);
$green-skill-card: hsl(127, 78%, 91%);
$green-heading-background: linear-gradient(
  $gradient-angle,
  $green-gradient-start $gradient-start,
  $green-gradient-end $gradient-end
);

$blue-gradient-start: hsl(198, 77%, 80%);
$blue-gradient-end: hsl(182, 83%, 93%);
$blue-skill-card: hsl(191, 76%, 91%);
$blue-heading-background: linear-gradient(
  $gradient-angle,
  $blue-gradient-start $gradient-start,
  $blue-gradient-end $gradient-end
);

@page {
  margin: 0;
  size: a4 portrait;
}

@mixin shadow-border {
  @media print {
    border: $default-border;
    border-bottom-width: 1.5px;
  }
}

h1,
h2,
h3,
h4,
h5 {
  break-after: avoid;
}

.table {
  background-color: $background;

  @media print {
    position: static;
  }

  & tr,
  & td {
    padding: 0;
  }
}

.relative {
  position: relative;
}

.available-badge-container {
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
}

article {
  position: relative;
  background: $background;
  color: $text-dark;
  font-size: 13px;
  line-height: 1.3;
  font-weight: 350;
  letter-spacing: 0.25px;
  overflow: hidden;
  font-family: "ABCWhyte", serif;
  font-feature-settings: normal;
  font-variant: normal;
  display: grid;
  width: $cv-width;

  @media not print {
    margin: auto;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  }

  @media print {
    overflow: visible;
  }
}

.cv-container {
  display: flex;
  gap: 4mm;
}

/*******************/
/*** Cv Controls ***/
/*******************/

#cv-controls {
  width: 60mm;
  top: 56mm;
  position: sticky;
  align-self: flex-start;
  @media print {
    display: none;
  }

  & > div {
    z-index: 10;
    display: grid;
    user-select: none;
    padding: 1.5em;
    border-radius: 1em;
    border: 1px solid #0006;
    width: 60mm;
    background: #f6f6f6;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  }
}

/*************/
/*** Utils ***/
/*************/

.stack {
  display: grid;

  &.center-items {
    align-items: center;
  }
}

.self-center {
  align-self: center;
}

.slim {
  padding: 0;
  margin: 0;
}

.flow {
  display: flex;

  &.from-right {
    flex-direction: row-reverse;
  }

  &.along-column {
    flex-direction: column;

    &.pad-12 > *:not(:first-child) {
      padding-top: 12mm;

      @media print {
        padding-top: 8mm;
      }
    }
    &.pad-10 > *:not(:first-child) {
      padding-top: 10mm;

      @media print {
        padding-top: 6mm;
      }
    }
  }

  &.wrap {
    flex-wrap: wrap;
  }

  &.flex-start {
    align-items: flex-start;
  }

  &.center-items {
    align-items: center;
  }
  &.flex-end {
    align-items: flex-end;
  }
}

.space-between {
  justify-content: space-between;
}

.space-evenly {
  justify-content: space-evenly;
}

.absolute {
  position: absolute;
}

.gap-0-35 {
  gap: 0.35mm;
}

.gap-1 {
  gap: 1mm;
}

.gap-1-5 {
  gap: 1.5mm;
}

.gap-2 {
  gap: 2mm;
}

.gap-4 {
  gap: 4mm;
}

.gap-6 {
  gap: 6mm;
}

.gap-8 {
  gap: 8mm;
}

.gap-12 {
  gap: 12mm;
}

.semi-bold {
  font-weight: 600;
}

.bold {
  font-weight: bold;
}

.content {
  padding: 8mm 14mm;
}

.selected-project-content {
  margin-top: 2mm;
  margin-bottom: 2mm;
}

h2.section-title {
  margin: 0 0 4.5mm 0;
  font-size: 1.1em;
  font-weight: 700;
  padding: 0 0 1mm 0;
  border-bottom: 0.75mm solid $theme-title-border;
}

section {
  a {
    color: $text-dark;
  }
}

.section-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  .education-title {
    font-weight: bold;
  }
}

span.title-card {
  display: inline-block;
  border-radius: 1mm;
  white-space: nowrap;
  padding: 1.5mm 2.5mm 1mm 2.5mm;
  border: 0.6mm solid $theme-title-card-border;
}

.break-avoiding {
  @media print {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

/***************/
/*** Heading ***/
/***************/

#heading {
  display: grid;
  grid-template-areas: "heading-grid-area";

  h1,
  h2,
  h3,
  h4,
  h5 {
    break-after: avoid;
  }

  .hint,
  .hint a {
    color: $text-dark-faded;
    text-decoration: $text-dark-faded;
  }
}

.theme-turquoise,
.theme-purple,
.theme-red,
.theme-yellow,
.theme-green,
.theme-blue {
  // Override for light themes
  #heading-content {
    grid-template-rows: 36mm auto;
  }
}

.theme-black {
  // Override for dark themes
  #heading {
    color: $text-light;

    h1,
    h2,
    h3,
    h4,
    h5 {
      color: $text-light;
    }
    .hint,
    .hint a {
      color: $text-light-faded;
      text-decoration: $text-light-faded;
    }
  }
  #heading-content {
    grid-template-rows: 36mm auto;
  }
}

.theme-turquoise #heading-background {
  background: $turquoise-heading-background;
}

.theme-purple #heading-background {
  background: $purple-heading-background;
}

.theme-red #heading-background {
  background: $red-heading-background;
}

.theme-yellow #heading-background {
  background: $yellow-heading-background;
}

.theme-green #heading-background {
  background: $green-heading-background;
}

.theme-blue #heading-background {
  background: $blue-heading-background;
}

.theme-black #heading-background {
  background: $black-heading-background;
}

#heading-background {
  background: $turquoise-heading-background;
  grid-area: heading-grid-area;
  z-index: 0;
  display: grid;
  grid-template-areas: "branding-placement";

  @media print {
    overflow: hidden;
  }

  #skry-background {
    grid-area: branding-placement;
  }

  #rebrand-big {
    grid-area: branding-placement;
    z-index: 1;
  }

  & .bg-logo-group {
    &:not(.temp-large-bg) {
      transform: translateX(64.5mm) translateY(-37.5mm) scale(0.45);
    }
    & .bg-logo-container {
      $gap: 0.18mm;
      $overlap: 33%;
      margin-left: 2mm;
      transform: translateX(-1mm) translateY(-4.5mm);
      gap: calc(3 * $gap);
      & .stacked-logo svg {
        margin: $gap 0;
        width: calc(100% + $overlap);
        height: 100%;
      }
      & .stacked-logo {
        transform: translateX(-$overlap);
      }
      &:nth-child(2) .stacked-logo {
        transform: translateY(-63.66%) translateX(-$overlap);
      }
      &:nth-child(3) > .stacked-logo {
        transform: translateY(-26.33%) translateX(-$overlap);
      }
      &:nth-child(4) > .stacked-logo {
        transform: translateY(11.33%) translateX(-$overlap);
      }
      &:nth-child(5) > .stacked-logo {
        transform: translateY(48.33%) translateX(-$overlap);
      }
    }
  }
}

#heading-triangle {
  background: $theme-triangle;
  clip-path: polygon(0 0, $triangle-size 0, 0 $triangle-size);
  grid-area: heading-grid-area;
  z-index: 1;
}

#heading-content {
  grid-area: heading-grid-area;
  z-index: 2;
  display: grid;
  gap: 5mm;
  font-size: 1.04em;
}

img.logo {
  height: 9mm;
}

h2.name {
  font-size: 8mm;
  margin: 3mm 0mm 0mm 0mm;
}

h3.cv-title {
  font-size: 3.5mm;
  margin-top: 2mm;
}

h3.skill-category-title {
  font-size: 3.5mm;
}
h3.featured-project-title {
  font-size: 3.75mm;
  margin-bottom: 3mm;

  &.truncate {
    display: block;
    max-width: none;
  }
}

div.background {
  font-size: 3mm;
  max-width: 75%;
  line-height: 5.5mm;
}

div.characteristics {
  height: 100%;
  max-height: 120px;
}

.right-arrow {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  &::before {
    content: "";
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTkuNDk5OTggMS41SDIuNDk5OThDMS45NDc3IDEuNSAxLjQ5OTk4IDEuOTQ3NzIgMS40OTk5OCAyLjVWOS41QzEuNDk5OTggMTAuMDUyMyAxLjk0NzcgMTAuNSAyLjQ5OTk4IDEwLjVIOS40OTk5OEMxMC4wNTIzIDEwLjUgMTAuNSAxMC4wNTIzIDEwLjUgOS41VjIuNUMxMC41IDEuOTQ3NzIgMTAuMDUyMyAxLjUgOS40OTk5OCAxLjVaIiBzdHJva2U9IndoaXRlIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5pam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTMuOTk5OTggNkg3Ljk5OTk4IiBzdHJva2U9IndoaXRlIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5pam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTUuOTk5OTggOEw3Ljk5OTk4IDZMNS45OTk5OCA0IiBzdHJva2U9IndoaXRlIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5pam9pbj0icm91bmQiLz4KPC9zdmc+Cg==");
    width: $right-arrow-spacing;
    height: $right-arrow-spacing;
    background-size: contain;
    background-repeat: no-repeat;
    margin-right: 2mm;
  }
}

.truncate {
  display: inline-block;
  vertical-align: middle;
  max-width: 40mm;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.separating-dot {
  &:not(:last-child)::after {
    content: "\2219"; // Small dot
    display: inline-block;
    width: $separating-dot-spacing;
    text-align: center;
  }

  /* This will create some spacing to properly center the dots */
  &:not(:first-child)::before {
    content: "";
    display: inline-block;
  }
}

.rebranding {
  img {
    max-height: 25mm;
    max-width: 60mm;
    margin: 20mm 10mm;
    border-radius: 1.5mm;
  }
}

/*******************/
/******* Cv ********/
/*******************/

#cv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  margin-top: 4mm;
  row-gap: 18mm;

  @media print {
    grid-template-rows: auto auto;
    row-gap: 10mm;
  }
}

#featured-project {
  grid-column: 1;
  min-width: 0;
  overflow: hidden;
}

#relevant-skills {
  grid-column: 2;
  grid-row: 1 / 3;
}

#education {
  grid-column: 1;
}

#projects {
  @media print {
    break-before: page;
    page-break-before: always;
  }
}

h3.title {
  font-size: 3.75mm;
  font-weight: 700;
}

/**************/
/*** Skills ***/
/**************/

.skill-theme-turquoise .skill-card {
  background: $turquoise-skill-card;
}

.skill-theme-purple .skill-card {
  background: $purple-skill-card;
}

.skill-theme-red .skill-card {
  background: $red-skill-card;
}

.skill-theme-yellow .skill-card {
  background: $yellow-skill-card;
}

.skill-theme-green .skill-card {
  background: $green-skill-card;
}

.skill-theme-blue .skill-card {
  background: $blue-skill-card;
}

.skill-theme-black .skill-card,
.skill-card.no-color {
  background: $black-skill-card;
}

.skill-card.raise {
  transform: translateY(-0.5mm);
  box-shadow: 0 0.75mm 0 0 $skill-card-shadow;
}

// TODO: Consider print-specific scaling for unusually large skill sets so a work experience
// remains within a single printed page.
.skill-card {
  border-radius: 10mm;
  padding: 0;
  text-align: center;
  vertical-align: center;
  background: $turquoise-skill-card;
}

/**************/
/*** Footer ***/
/**************/

.page-footer,
.page-footer-space {
  height: $footer-height;
}

tfoot {
  @media print {
    display: table-footer-group;
  }
}

.page-footer {
  background: $footer-background;
  color: $footer-text;
  position: fixed;
  bottom: 0;
  width: $cv-width;
  vertical-align: center;
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  justify-content: space-between;

  @media not print {
    position: relative;
  }

  p {
    font-size: 1.25em;
    margin-bottom: 0.25em;
  }

  div {
    display: flex;
    height: 100%;
    span {
      margin: auto;
    }
  }
}
</style>
